import { AlertPhone, RuuviTag, UserAlertRule, UserAlertRuleInput } from '../../types'
import { useTranslation } from 'react-i18next'
import { updateAlertPhoneAlertRules } from '../../services/alertPhoneService'
import { AlertGroupName, AlertRuleName } from '../../enums'
import { ChangeEvent, useState } from 'react'
import { useDispatch } from 'react-redux'
import {
  createErrorNotification,
  createSuccessNotification
} from '../../redux/slices/notifications'
import { usePolicy } from '../../contexts/PolicyContext'
import { IconButton, Switch, SwitchProps, Tooltip } from '@mui/material'
import { Warning } from '@mui/icons-material'

export interface AlertPhoneAlertRuleEnableSwitchProps extends Omit<
  SwitchProps,
  'children' | 'checked'
> {
  alertPhone: AlertPhone
  ruuviTag?: RuuviTag
  alertRules: UserAlertRule[]
  ruleGroupName: AlertGroupName
  alertRuleName: AlertRuleName
  beforeEnable?: (input: UserAlertRuleInput[]) => void
  afterEnable?: (alertPhoneAlertRules: UserAlertRule[]) => void
}

export default function AlertPhoneAlertRuleEnableSwitch({
  alertPhone,
  ruuviTag,
  alertRules,
  ruleGroupName,
  alertRuleName,
  beforeEnable,
  afterEnable,
  size,
  disabled,
  ...props
}: AlertPhoneAlertRuleEnableSwitchProps) {
  /**
   * The translate function.
   */
  const { t } = useTranslation('common')

  /**
   * The policy state.
   */
  const { canEditAlertPhone } = usePolicy()

  /**
   * The dispatch function.
   */
  const dispatch = useDispatch()

  /**
   * The loading state.
   */
  const [loading, setLoading] = useState<boolean>(false)

  /**
   * The checked state.
   */
  const [checked, setChecked] = useState<boolean>(false)

  /**
   * Indicates whether the alert rule can be enabled for the alert phone.
   */
  const canEnableAlertPhoneAlertRule =
    Boolean(alertPhone.phoneNumber) && alertPhone.phoneVerifiedAt !== null

  /**
   * Handle the "enable" action.
   */
  async function handleChange(_event: ChangeEvent<HTMLInputElement>, checked: boolean) {
    if (checked) {
      try {
        setLoading(true)

        const input: UserAlertRuleInput[] = [
          ...alertRules.map((alertRule) => ({
            ruleGroupName: alertRule.ruleGroupName,
            alertRuleName: alertRule.alertRuleName,
            emailNotificationEnabled: false,
            smsNotificationEnabled: alertRule.smsNotificationEnabled,
            appNotificationEnabled: false
          })),
          {
            ruuviTagId: ruuviTag?.id,
            ruleGroupName,
            alertRuleName,
            emailNotificationEnabled: false,
            smsNotificationEnabled: alertPhone.phoneVerifiedAt !== null,
            appNotificationEnabled: false
          }
        ]

        beforeEnable?.(input)

        const updatedAlertRules = await updateAlertPhoneAlertRules(alertPhone.id, input)

        // Turn the switch "on" after success.
        setChecked(true)

        // Wait for the animation to end for smoother transition.
        setTimeout(() => {
          dispatch(createSuccessNotification(t('enableSmsNotificationSuccess')))
          afterEnable?.(updatedAlertRules)
        }, 50)
      } catch (_error) {
        // Turn the switch "off" if there is an error.
        setChecked(false)

        dispatch(createErrorNotification(t('enableSmsNotificationError')))
      } finally {
        setLoading(false)
      }
    }
  }

  if (!canEditAlertPhone(alertPhone)) {
    return null
  }

  return (
    <>
      {canEnableAlertPhoneAlertRule ? (
        <Switch
          size={size}
          disabled={loading || disabled}
          {...props}
          checked={checked}
          onChange={handleChange}
        />
      ) : (
        <Tooltip title={t('phoneIsNotVerified')}>
          <IconButton size={size} color="warning">
            <Warning />
          </IconButton>
        </Tooltip>
      )}
    </>
  )
}
